<template>
        <section class="summary-section cart-page-summary" aria-labelledby="summary-heading">
            <div class="summary-container cart">
                <div class="container-header">
                    <h3 id="summary-heading" class="headline-s">Summary</h3>
                </div>
                <dl>
                    <dt class="paragraph-default" aria-label="Subtotal amount">Subtotal</dt>
                    <dd class="paragraph-default">{{ currencySymbol }}{{ subTotal }}</dd>
                </dl>
                <dl v-if="isPropertyVisible('discounts') && discount > 0">
                    <dt class="paragraph-default" aria-label="Discounts amount">Discounts</dt>
                    <dd class="paragraph-default discount-price">-{{ currencySymbol }}{{ discount }}</dd>
                </dl>
                <dl>
                    <dt class="paragraph-default" aria-label="Taxes status">Taxes</dt>
                    <dd :class="{
                        'paragraph-default': true,
                        'font-italic': true
                    }">{{ isTaxExempt ? 'Tax Exempt' : 'Calculated at Checkout' }}</dd>
                </dl>
                <dl>
                    <dt class="paragraph-default" aria-label="Shipping status">Shipping</dt>
                    <dd class="paragraph-default font-italic">{{ shippingStatusLabel }}</dd>
                </dl>
                <dl class="order-total">
                    <dt class="title-l" aria-label="Estimated total amount">Estimated Total</dt>
                    <dd class="title-l">{{ currencySymbol }}{{ subTotal }}</dd>
                </dl>
                <div class="bnpl-info" v-if="isPropertyVisible('bnlp_message')">
                    <p class="paragraph-details text-rothcoBlack">From $97.79/mo with BNPL</p>
                    <a href="#" class="link-400" aria-label="Learn more about Buy Now, Pay Later">Learn More</a>
                </div>
                <div class="apply-promo-code-section" aria-label="Promo code application information" v-if="isPropertyVisible('apply_promo_later')">
                    <p class="font-italic text-rothcoBlack">You will be able to apply a promo code to your order during checkout.</p>
                </div>

                <ApplyCoupon :couponCode="couponCode" class="mt-6" v-if="isPropertyVisible('apply_coupon')"/>

                <!-- Stripe Express Checkout (only for D2C) -->
                <div v-if="showExpressCheckout" class="stripe-express-checkout-cart">
                    <p class="paragraph-details text-center stripe-express-checkout-heading">Secure 1-Click Checkout</p>
                    <StripeExpressCheckoutButton
                        v-if="pricingReady"
                        ref="stripeExpressCheckoutButtonRef"
                        :stripe-publishable-key="stripePublishableKey"
                        :amount="orderSubTotal"
                        :discount-amount="couponDiscountAmount"
                        :has-restrictions="props.hasRestrictions"
                        currency="usd"
                        :allowed-countries="['US']"
                        :disabled="!allowExpressCheckout"
                        :shipping-rates="shippingRates"
                        :express-checkout="true"
                        :list-id="list.list_id"
                        v-model:is-express-checkout-available="isStripeExpressCheckoutAvailable"
                        v-model:restriction-context="restrictionContext"
                        @payment-success="handleStripeExpressCheckoutSuccess"
                    />
                    <p class="paragraph-caption text-center text-rothcoText stripe-express-checkout-caption">Faster, safer, encrypted</p>
                    <p class="input-support-text invalid-state paragraph-details text-center mt-2" v-if="props.hasRestrictions">
                        Some items in your cart are under a restriction. Please remove restricted items to use Express Checkout.
                    </p>
                </div>

                <div v-if="showExpressCheckout && canPlaceOrders" class="checkout-option-divider" aria-hidden="true">
                    <span class="paragraph-caption text-rothcoText">Or continue with standard checkout</span>
                </div>

                <div v-if="canPlaceOrders" class="checkout-button mobile-hidden-flex">
                    <Link as="button" :disabled="!allowCheckout"  :href="checkoutRoute" class="primary" aria-label="Proceed to checkout">
                        Checkout
                        <div aria-hidden="true" class="icon">
                            <CarretsBlack alt="carrets right" />
                        </div>
                    </Link>
                </div>

                <div v-if="canPlaceOrders" class="checkout-button desktop-hidden-flex">
                    <RothcoCta v cta-type="button" :disabled="!allowCheckout" :href="checkoutRoute" class="primary" aria-label="Proceed to checkout">
                        Checkout
                    </RothcoCta>
                </div>
                <a href="#" @click.prevent="handleAccountLogin" class="link-400 dealer-checkout-link" aria-label="Proceed to Dealer Checkout" v-if="isPropertyVisible('dealer_checkout_btn') && !isLoggedIn">Dealer Checkout</a>
                <div  v-if="canPlaceOrders"  class="cards-section" aria-label="Accepted payment methods">
                        <VisaIcon alt="Visa logo"/>
                        <MastercardIcon alt="MasterCard logo"/>
                        <template v-if="isPropertyVisible('show_b2c_cards')">
                            <AmexIcon alt="American Express logo"/>
                            <DiscoverIcon alt="Discover logo"/>
                            <PaypalIcon alt="PayPal logo"/>
                        </template>
                </div>
            </div>
        </section>
</template>

<script lang="ts" setup>
    import { computed, inject, watch, onMounted } from "vue";
    import { Link, router } from "@inertiajs/vue3";
    // import { route } from "ziggy-js";
    import { useStorage } from '@vueuse/core';
    import { useSettingsStore } from "@/store/settings";
    import { useCartDetails } from "@/helpers/composables/useCartDetails";
    import { storeToRefs } from "pinia";
    import RothcoCta from "@/components/RothcoCta.vue";
    import ApplyCoupon from "@/components/cart/ApplyCoupon.vue";
    import CarretsBlack from "@icons/carrets-black.svg";
    import VisaIcon from "@icons/visa.svg";
    import MastercardIcon from "@icons/mastercard.svg";
    import AmexIcon from "@icons/amex.svg";
    import DiscoverIcon from "@icons/discover.svg";
    import PaypalIcon from "@icons/paypal.svg";
    import StripeExpressCheckoutButton from "@/components/StripeExpressCheckoutButton.vue";
    import { useForm } from "@inertiajs/vue3";
    import { ref } from "vue";
    import { useCheckoutD2CStore } from "@/store/checkoutD2CStore";
    import { useFetch } from "@/helpers/composables/useFetch";

    const restrictionContext = defineModel('restrictionContext', {
        type: Object,
        default: () => ({
            region: null,
            postalCode: null,
            shippingMethod: null,
        })
    })

    let props = defineProps({
        list: {
            type: Object,
            default: ()=>({})
        },
        isTaxExempt: {
            type: Boolean,
            default: false
        },
        hasRestrictions: {
            type: Boolean,
            default: false
        },
        stripePublishableKey: {
            type: String,
            default: null
        },
        shippingRates: {
            type: Object,
            default: {}
        }
    });
    let route = inject("route");
    const stopCartPageSentryReplay = inject<(() => void) | null>("stopCartPageSentryReplay", null);
    let list = computed(() => {
        return props.list;
    });

    const couponCode = useStorage("coupon",null);
    const { applyCouponAndRecalculate } = useCheckoutD2CStore();
    const { couponInformation } = storeToRefs(useCheckoutD2CStore());

    let { isPropertyVisible, currencySymbol, subTotal } = useCartDetails(null, list, 'summary');

    let discount = computed(() => {
        return (list.discount ?? 0).toFixed(2);
    });

    let settings = useSettingsStore();


    const { toggleFlyout, setOverlayMessage } = settings;

    let { is_dealer, freeShippingProgressPercentage, freeShippingByCoupon, isLoggedIn, canSeePricing, canPlaceOrders } = storeToRefs(settings);

    let isB2B = computed(() => is_dealer.value);

    let checkoutRoute = computed(() => {
        return is_dealer.value ? route("checkout.express") : route('shop.show');
    });

    let showExpressCheckout = computed(() => {
        return isStripeExpressCheckoutAvailable.value && !isB2B.value && props.stripePublishableKey;
    });

    const shippingStatusLabel = computed(() => {
        if (!isB2B.value && (freeShippingProgressPercentage.value === 100 || freeShippingByCoupon.value || couponInformation.value?.freeShipping)) {
            return "FREE";
        }

        return  "Calculated at Checkout";
    });

    const listItemErrors = inject('listItemErrors');

    const allowCheckout = computed(() => {
        if(isB2B.value) {
            return true
        }

        return Object.keys(listItemErrors).length == 0;
    });

    const allowExpressCheckout = computed(() => {
        if (isB2B.value) return false;
        return allowCheckout.value && !props.hasRestrictions;
    });

    const handleAccountLogin = () => {
        toggleFlyout();
    };

    // Stripe Express Checkout
    const stripeExpressCheckoutButtonRef = ref(null);
    const isStripeExpressCheckoutAvailable = ref(true);
    const pricingReady = ref(false);

    const couponAddedItemSubtotal = computed(() => {
        return couponInformation.value?.items
            ?.filter(item => item.item_added_by_coupon === true)
            .reduce((sum, item) => sum + ((+item.priceWithoutDiscount) || 0), 0) ?? 0;
    });
    const couponDiscountAmount = computed(() => {
        return +(couponInformation.value?.discountAmount ?? 0);
    });
    const orderSubTotal = computed(() => {
        return parseFloat(list.value.total ?? 0) + couponAddedItemSubtotal.value;
    });

    const refreshCartPricing = async () => {
        try {
            await useFetch(route('endpoint.stripe-express-checkout.refresh-pricing'), {
                onFetchError(ctx) {
                    return ctx;
                }
            }).post({});
        } catch (e) {}
    };

    onMounted(async () => {
        if (!showExpressCheckout.value) {
            return;
        }

        await refreshCartPricing();
        await router.reload({ only: ['list', 'list_items'] });
        pricingReady.value = true;
    });

    watch([couponCode, pricingReady], ([newCouponCode]) => {
        if (showExpressCheckout.value && !pricingReady.value) {
            return;
        }

        if (!newCouponCode) {
            couponInformation.value = {};
            return;
        }

        applyCouponAndRecalculate(newCouponCode, true);
    }, { immediate: true });

    const handleStripeExpressCheckoutSuccess = async (paymentData) => {
        try {
            const orderData = {
                payment_method: 'stripe_express_checkout',
                stripe_express_checkout: true,
                stripe_payment_intent_id: paymentData.payment.paymentIntentId,
                used_coupon: couponCode.value || null,
                source: 'cart',
            };

            setOverlayMessage("We're processing your order. Please do not refresh your screen.");

            await useForm(orderData).post(route('shop.process'), {
                onSuccess: () => {
                    stopCartPageSentryReplay?.();
                },
                onError: (errors) => {
                    setOverlayMessage(null);
                    console.error('Order submission failed:', errors);
                },
                onFinish: () => {
                    setOverlayMessage(null);
                }
            });

        } catch (error) {
            setOverlayMessage(null);
            console.error('Error processing Stripe Express Checkout:', error);
        }
    };
</script>